
import { useState, useEffect, useCallback } from 'react';

/**
 * Hook that detects connection quality and provides appropriate UI adjustments
 * @returns Object with connection quality information and helpers
 */
export function useConnectionQuality() {
  const [isSlowConnection, setIsSlowConnection] = useState(false);
  const [connectionType, setConnectionType] = useState<string>('unknown');
  const [saveDataMode, setSaveDataMode] = useState(false);
  const [effectiveDownlink, setEffectiveDownlink] = useState<number | null>(null);
  const [roundTripTime, setRoundTripTime] = useState<number | null>(null);
  
  const checkConnectionQuality = useCallback(() => {
    // Check if the navigator API supports connection information
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      
      if (connection) {
        // Determine connection type and quality
        setConnectionType(connection.effectiveType || 'unknown');
        setSaveDataMode(connection.saveData === true);
        setEffectiveDownlink(connection.downlink || null);
        setRoundTripTime(connection.rtt || null);
        
        setIsSlowConnection(
          connection.effectiveType === '2g' || 
          connection.effectiveType === 'slow-2g' ||
          connection.downlink < 1 ||  // Less than 1 Mbps
          connection.rtt > 500 ||     // Round-trip time > 500ms
          connection.saveData === true
        );
      }
    } else {
      // Fallback detection based on performance API
      try {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigation) {
          const loadTime = navigation.loadEventEnd - navigation.startTime;
          setIsSlowConnection(loadTime > 3000); // Assume slow if page load > 3s
        }
      } catch (e) {
        console.log('Performance API not supported');
      }
    }
  }, []);
  
  useEffect(() => {
    // Initial check
    checkConnectionQuality();
    
    // Set up monitoring for connection changes
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      
      if (connection) {
        connection.addEventListener('change', checkConnectionQuality);
        return () => {
          connection.removeEventListener('change', checkConnectionQuality);
        };
      }
    }
    
    // Add performance observer as another way to detect slow connections
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length > 0) {
          // If LCP is over 2.5s, we might have a slow connection
          const lastEntry = entries[entries.length - 1];
          if (lastEntry.startTime > 2500) {
            setIsSlowConnection(true);
          }
        }
      });
      
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      
      return () => {
        observer.disconnect();
      };
    } catch (e) {
      console.log('Performance Observer not supported');
    }
    
  }, [checkConnectionQuality]);
  
  // Helper functions
  const getImageQuality = useCallback(() => {
    if (isSlowConnection) return 'low';
    if (connectionType === '4g') return 'high';
    return 'medium';
  }, [isSlowConnection, connectionType]);
  
  const shouldLazyLoadComponents = useCallback(() => {
    return isSlowConnection || saveDataMode;
  }, [isSlowConnection, saveDataMode]);
  
  const getOptimalPageSize = useCallback((defaultSize: number = 20, minSize: number = 5): number => {
    if (isSlowConnection || saveDataMode) return minSize;
    if (connectionType === '4g' && !saveDataMode) return defaultSize;
    return Math.floor(defaultSize / 2);
  }, [isSlowConnection, saveDataMode, connectionType]);
  
  const getConnectionDetails = useCallback(() => {
    return {
      type: connectionType,
      saveData: saveDataMode,
      downlink: effectiveDownlink,
      rtt: roundTripTime,
      slow: isSlowConnection
    };
  }, [connectionType, saveDataMode, effectiveDownlink, roundTripTime, isSlowConnection]);
  
  return {
    isSlowConnection,
    connectionType, 
    saveDataMode,
    getImageQuality,
    shouldLazyLoadComponents,
    getOptimalPageSize,
    getConnectionDetails
  };
}

// For backward compatibility
export const useConnectionQualityCheck = useConnectionQuality;
